import { useSEO } from "@/hooks/useSEO";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { ChapterHeading } from "@/components/shared/ChapterHeading";
import { StatStrip } from "@/components/shared/StatStrip";
import { IconTile } from "@/components/shared/IconTile";
import { TIMELINE, PILLARS, FOUNDER, COLORS, ABOUT_STORY_IMAGES } from "@/data/siteData";

export default function About() {
  useSEO(
    "About | The Market Place Jaipur",
    "Founded in 2019 by Upasana Bajaj Kumar, The Market Place is Jaipur's community-led platform for homegrown businesses, entrepreneurs and makers."
  );

  return (
    <div data-testid="about-page">
      <section className="pt-16 pb-16 px-6 sm:px-8 max-w-4xl mx-auto text-center">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#D81B60]">About</span>
          <h1 className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">
            Built in Jaipur. Built for Its Businesses.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            The Market Place (formerly known as The Farmers Market) is a community-first platform created to support the people building businesses of their own.
          </p>
        </Reveal>
      </section>

      {/* Founder — leads the story */}
      <section className="pb-12 sm:pb-16 px-6 sm:px-8 max-w-6xl mx-auto">
        <Reveal>
          <div className="grid md:grid-cols-[340px_1fr] gap-10 items-start rounded-3xl bg-gradient-to-br from-[#FDF2F5] to-white border border-gray-100 p-8 sm:p-12">
            <img src={FOUNDER.photo} alt="Upasana Bajaj Kumar, founder of The Market Place, Jaipur" data-testid="founder-photo" className="rounded-2xl w-full aspect-[4/5] object-cover" />
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#D81B60]">Founder</span>
              <h2 className="font-extrabold text-3xl sm:text-4xl mt-2 mb-1">{FOUNDER.name}</h2>
              <p className="text-[#D81B60] font-semibold mb-6">Founder, The Market Place</p>
              <p className="text-lg leading-relaxed text-gray-700">
                Upasana Bajaj Kumar founded The Market Place in 2019 to create meaningful opportunities for Jaipur's
                homegrown businesses. What began with organic farmers and independent brands has evolved into a
                thriving community of entrepreneurs, makers and customers.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-gray-700">
                Under her leadership, The Market Place has conducted 400+ markets, supported 1,000+ businesses, and
                grown through trust, inclusion and consistent community-building.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* How It Began / Growing Through Change / Becoming TMP */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-5xl mx-auto space-y-20">
        <Reveal>
          <h2 className="font-extrabold tracking-tight text-4xl sm:text-5xl text-center mb-4">Our Story</h2>
        </Reveal>
        <Reveal>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#1F6F50]">2019</span>
              <h2 className="font-extrabold text-2xl sm:text-3xl mt-2 mb-4">How It Began</h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                The Market Place was founded by Upasana Bajaj Kumar in 2019, beginning within Jaipur's farmers market
                ecosystem on JLN Marg. The idea was to create a space where organic produce farmers and homegrown organic
                brands could interact directly with customers.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg mt-4">
                It started small, but the purpose was clear from the beginning: make it easier for local businesses and
                the people looking for them to find one another.
              </p>
            </div>
            <img src={ABOUT_STORY_IMAGES[0]} alt="The Market Place, Jaipur — 2019" className="rounded-3xl w-full aspect-[4/3] object-cover order-first md:order-last" />
          </div>
        </Reveal>
        <Reveal>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <img src={ABOUT_STORY_IMAGES[1]} alt="The Market Place, Jaipur — Growing through change" className="rounded-3xl w-full aspect-[4/3] object-cover" />
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#E2725B]">COVID Period</span>
              <h2 className="font-extrabold text-2xl sm:text-3xl mt-2 mb-4">Growing Through Change</h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                When COVID-19 disrupted physical retail, the community needed a different kind of support. The model
                adapted to delivery-led selling, working with existing businesses to take their products directly to customers.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg mt-4">
                It reinforced something that continues to define The Market Place today: the platform exists not simply
                to conduct markets, but to support the businesses within its community when they need it.
              </p>
            </div>
          </div>
        </Reveal>
        <Reveal>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#D81B60]">2023</span>
              <h2 className="font-extrabold text-2xl sm:text-3xl mt-2 mb-4">Becoming The Market Place</h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                In 2023, the initiative evolved into The Market Place. The scope expanded beyond organic produce and
                food to include Jaipur's wider homegrown business ecosystem.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg mt-4">
                Today, the community includes first-time founders, independent makers, growing labels, experienced
                entrepreneurs, sustainable businesses and local brands across multiple categories.
              </p>
            </div>
            <img src={ABOUT_STORY_IMAGES[2]} alt="The Market Place, Jaipur — Today" className="rounded-3xl w-full aspect-[4/3] object-cover order-first md:order-last" />
          </div>
        </Reveal>
      </section>

      {/* Our Belief */}
      <section className="py-16 px-6 sm:px-8">
        <div className="max-w-3xl mx-auto text-center bg-[#FDFBF7] rounded-[2rem] py-16 px-6 sm:px-12">
        <Reveal>
          <h2 className="font-extrabold text-3xl sm:text-4xl mb-6">Our Belief</h2>
          <p className="font-extrabold text-2xl sm:text-3xl text-[#D81B60] mb-6">To Jaipur. From Jaipur.</p>
          <p className="text-gray-500 mb-1">Jaipur has always been central to The Market Place.</p>
          <div className="text-lg text-gray-700 space-y-1 mt-4">
            <p>The businesses are built here.</p>
            <p>The ideas begin here.</p>
            <p>The customers support them here.</p>
            <p>The relationships grow here.</p>
          </div>
          <p className="mt-6 text-gray-500">The Market Place exists to strengthen that cycle.</p>
        </Reveal>
        </div>
      </section>

      {/* What We Stand For */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto">
          <ChapterHeading number="01" title="What We Stand For" />
          <RevealStagger className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {PILLARS.map((p, i) => (
              <RevealItem key={p.title}>
                <div data-testid={`pillar-${p.title.toLowerCase()}`} className="rounded-2xl border border-gray-200 bg-white p-6 h-full hover:shadow-lg transition-shadow duration-300">
                  <IconTile icon={p.icon} title={p.title} desc={p.desc} color={COLORS[i % COLORS.length]} />
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Journey */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-5xl mx-auto">
        <ChapterHeading number="02" title="Our Journey So Far" />
        <div className="space-y-10">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.year} delay={i * 0.05}>
              <div data-testid={`timeline-item-${i}`} className="grid sm:grid-cols-5 gap-4 border-t-2 pt-6" style={{ borderColor: COLORS[i % COLORS.length] }}>
                <div className="font-mono text-sm font-bold uppercase tracking-wide">
                  <span style={{ color: COLORS[i % COLORS.length] }}>{t.year}</span>
                </div>
                <div className="sm:col-span-3">
                  <h3 className="font-extrabold text-2xl mb-2">{t.title}</h3>
                  <p className="text-gray-500 leading-relaxed text-lg">{t.desc}</p>
                </div>
                <img src={t.image} alt={`The Market Place, Jaipur — ${t.year}`} className="rounded-xl w-full aspect-square object-cover" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 border-y border-gray-200 bg-[#FDFBF7]">
        <StatStrip />
      </section>

      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-3xl mx-auto text-center">
        <Reveal>
          <h2 className="font-extrabold text-3xl sm:text-4xl">Community. Purpose. Growth.</h2>
          <p className="mt-4 text-gray-500 text-lg">Those three ideas continue to shape every market, relationship and collaboration we build.</p>
        </Reveal>
      </section>
    </div>
  );
}
